import { DictionaryRecordContextProvider } from "@/components/dictionaries/DictionaryContext";
import DictionaryPageHeader from "@/components/dictionaries/DictionaryPageHeader";
import { useToast } from "@/components/ui/use-toast";
import { Module } from "@/constants";
import { useQueryCacheUpdater } from "@/hooks/useQueryCacheUpdater";
import { useFormUnsavedChangesWarning } from "@/hooks/useFormUnsavedChangesWarning";
import { ApiError } from "@/services/api";
import {
  ReadPayrollDepartment,
  WritePayrollDepartment,
} from "@/types/payrollDepartment";
import { zodResolver } from "@hookform/resolvers/zod";
import { MutationFunction, useMutation } from "@tanstack/react-query";
import { exists } from "i18next";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useModulePermissions } from "@/modules/account/hooks/useModulePermissions";
import { usePayrollDepartmentsDictionary } from "@/modules/payrollDepartments/hooks/usePayrollDepartmentsDictionary";
import PayrollDepartmentForm from "./PayrollDepartmentForm";

export default function PayrollDepartmentCreator() {
  const id = "new";
  const { t } = useTranslation();
  const config = usePayrollDepartmentsDictionary();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { invalidateListQuery } = useQueryCacheUpdater(
    Module.PayrollDepartments,
  );
  const labels = config.getLabels(t);
  const { can_create } = useModulePermissions();
  const mutationFn: MutationFunction<
    ReadPayrollDepartment,
    WritePayrollDepartment
  > = async (values) => config.service.post(values);

  const mutation = useMutation<
    ReadPayrollDepartment,
    ApiError,
    WritePayrollDepartment
  >({
    mutationFn,
    onSuccess: (response) => {
      const id = response.id;
      // Reset form with current values to clear dirty state This is needed because
      // ShiftReportDepartmentSelect loads data asynchronously, which updates the field after form
      // initialization and marks it as dirty. Without this reset, the Submit button would remain
      // enabled after successful save.
      form.reset(form.getValues());
      navigate(`${config.path}/${id}`);
      toast({
        title: labels.success.create,
      });
      invalidateListQuery();
    },
    onError: ({ errorCode }) => {
      const description = exists(`errors.${errorCode}`)
        ? t(`errors.${errorCode}`)
        : t("errors.unknownError");
      toast({
        title: labels.errors.create,
        description,
        variant: "destructive",
      });
    },
  });

  const form = useForm<WritePayrollDepartment>({
    resolver: zodResolver(config.schema),
    defaultValues: config.defaultValues,
    disabled: mutation.isPending || !can_create,
  });

  useFormUnsavedChangesWarning(form);

  return (
    <DictionaryRecordContextProvider
      value={{
        config,
        mutation,
        form,
        id,
      }}
    >
      <DictionaryPageHeader />
      <PayrollDepartmentForm />
    </DictionaryRecordContextProvider>
  );
}
